import React, { useEffect, useState } from 'react'
import { useTheme } from '../../../../components/hooks/useTheme';
import useAuth from '../../../../components/hooks/useAuth';
import { Button, Dialog } from '@mui/material';
import axios from 'axios';
import baseURL from '../../../../configs/api';
import PleaseWaitPopup from '../../../PleaseWaitPopup';
import colorConfigs from '../../../../configs/colorConfigs';
import CustomFormatDatePicker from '../../../../components/common/CustomFormatDatePicker';
import { validateMin } from '../../../../utils/validateMin';
import useGeneral from '../../../../components/hooks/useGeneral';
import Cookies from 'js-cookie';
import useTenant from '../../../../components/hooks/useTenant';

type AddRelocationProps = {
    idNumber: any;
    openAddRelocation: boolean;
    setOpenAddRelocation: (value: boolean) => void;
    setRefreshRelocationList: (value: boolean) => void;
    vehicleDetails: {
        currentLocationId: string;
        odometer: number;
        fuelLevel: string;
    };
}

export const AddRelocation = ({ idNumber, openAddRelocation, setOpenAddRelocation, setRefreshRelocationList, vehicleDetails }: AddRelocationProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { dateFormat }: any = useGeneral();
    const [relocationVehicleDate, setRelocationVehicleDate] = useState<string>("");
    const [relocationVehicleTime, setRelocationVehicleTime] = useState<string>("");
    const [toLocationId, setToLocationId] = useState<string>("");
    const [toLocations, setToLocations] = useState<any[]>([]);
    const [newOdometer, setNewOdometer] = useState<number>(0);
    const [newFuelLevel, setNewFuelLevel] = useState<string>("");
    const [status, setStatus] = useState<string>("1");
    const [reason, setReason] = useState<string>("");
    const [pleaseWait, setPleaseWait] = useState<boolean>(false);

    useEffect(() => {
        axios
            .get(`${baseURL}location`, {
                headers: {
                    'X-Tenant-ID': tenantId,
                }
            })
            .then((response) => {
                const filteredLocations = response?.data?.filter(
                    (item: any) => item.id !== vehicleDetails?.currentLocationId
                );

                setToLocations(() =>
                    filteredLocations?.map((item: any) => ({
                        value: item.id,
                        label: item.locationName,
                    }))
                );

                setToLocationId(filteredLocations[0]?.id || "");
                handleTenantError(null);
            })
            .catch((error) => {
                setToLocations([]);
                setToLocationId("");
                handleTenantError(error);
            });

             // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleDetails?.currentLocationId]);

    useEffect(() => {
        if (vehicleDetails) {
            setNewOdometer(vehicleDetails?.odometer || 0);
            setNewFuelLevel(vehicleDetails?.fuelLevel || "0");
        }
    }, [vehicleDetails]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!pleaseWait) {
            setPleaseWait(true);

            axios
                .post(
                    `${baseURL}relocation?userId=${auth?.id}`,
                    JSON.stringify({
                        vehicleId: idNumber?.state,
                        fromLocationId: vehicleDetails?.currentLocationId,
                        toLocationId,
                        reason,
                        oldOdometer: vehicleDetails.odometer,
                        newOdometer,
                        oldFuelLevel: vehicleDetails.fuelLevel,
                        newFuelLevel,
                        relocationDate: new Date(relocationVehicleDate + 'T' + relocationVehicleTime)?.toLocaleString(),
                        status,
                        createdAt: new Date().toLocaleString(),
                        createdBy: auth?.givenname,
                    }),
                    {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                        withCredentials: true,
                    }
                )
                .then(() => {
                    setPleaseWait(false);
                    setOpenAddRelocation(false);
                    setRefreshRelocationList(true);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        }
    }

    return (
        <>
            <Dialog
                open={openAddRelocation}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '550px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>

                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Add Relocate Vehicle</h2>

                                    <span className={`material-symbols-outlined text-[25px] font-light cursor-pointer ${themeStyles.main.textHeadingColor}`} onClick={() => setOpenAddRelocation(false)}>close</span>

                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="grid grid-cols-2 gap-6 mt-4">
                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Relocation Date</label>
                                                <CustomFormatDatePicker
                                                    value={relocationVehicleDate}
                                                    onChange={setRelocationVehicleDate}
                                                    dateFormat={dateFormat}
                                                    required={true}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Relocation Time</label>
                                                <input
                                                    type="time"
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={relocationVehicleTime}
                                                    onChange={(e) => {
                                                        setRelocationVehicleTime(e.target.value)
                                                    }}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>To</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={toLocationId}
                                                    onChange={(e) => setToLocationId(e.target.value)}
                                                    required
                                                >
                                                    {toLocations.map((item: any) => {
                                                        return <option className={`${themeStyles.main.tailwindSectionBg}`} key={item.value} value={item.value}>{item.label}</option>
                                                    })}
                                                </select>

                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Status</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={status}
                                                    onChange={(e) => setStatus(e.target.value)}
                                                    required
                                                >
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"1"}>Not Started</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"2"}>In Transit</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={"3"}>Completed</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Odometer</label>
                                                <input
                                                    type="number"
                                                    min={0}
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={newOdometer}
                                                    onChange={(e) => setNewOdometer(validateMin(e.target.value, 0))}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Fuel Level</label>
                                                <select
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={newFuelLevel}
                                                    onChange={(e) => setNewFuelLevel(e.target.value)}
                                                    required
                                                >
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={0}>0</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={1}>1</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={2}>2</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={3}>3</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={4}>4</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={5}>5</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={6}>6</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={7}>7</option>
                                                    <option className={`${themeStyles.main.tailwindSectionBg}`} value={8}>8</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="w-full mt-4">
                                        <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reason</label>
                                        <textarea
                                            className={`w-full px-[8px] py-[8px]  border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                            rows={6}
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                        />
                                    </div>

                                    <div className="mt-4 w-full flex gap-4">
                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.main.bg
                                            }}
                                            type="submit"
                                        >
                                            Save
                                        </Button>

                                        <Button variant="contained"
                                            sx={{
                                                backgroundColor: colorConfigs.btn.cancel.bg,
                                                border: colorConfigs.btn.cancel.border,
                                                color: colorConfigs.btn.cancel.color
                                            }}
                                            onClick={() => setOpenAddRelocation(false)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWait} />
        </>
    )
}